import { FC, ReactElement, memo, useCallback } from 'react';
import { useIsMutating, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { useAppPagination } from '@/common/hooks';
import { AppRoutes } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import { DomainsQueryKeys, useDeleteDomainsMutation, useGetByIdDomainsQuery, useUpdateDomainDeployStatus, useUpdateDomainHealthStatus } from '@/modules/Domains';

import { DomainsOverviewView } from './DomainsOverviewView';

interface Props {
  domainId: string;
}

export const DomainsOverviewContainer: FC<Props> = memo(({ domainId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const { pageParam } = useAppPagination();

  const {
    data: domainData,
    error: domainError,
    isLoading: isDomainLoading,
    isFetching: isDomainFetching,
    isError: isDomainError,
  } = useGetByIdDomainsQuery(domainId)({
    enabled: !!domainId
  });

  const { mutate: deleteDomainMutation, isLoading: isDeleteDomainLoading } = useDeleteDomainsMutation(domainId)({
    onSuccess: async () => {
      await queryClient.refetchQueries([DomainsQueryKeys.GetAll], { exact: false });

      appMessageApi.success('Домен успішно видалено!');

      navigate(AppRoutes.Domains);
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    }
  });

  useUpdateDomainHealthStatus({
    domainId,
    pageParam: { page: pageParam },
  });

  useUpdateDomainDeployStatus({
    domainId,
    domainName: null,
  });

  const isDomainDetachFetching = !!useIsMutating({
    mutationKey: [DomainsQueryKeys.DetachConfig, domainId],
    exact: true
  });

  const handleDeleteDomain = useCallback((): void => {
    deleteDomainMutation();
  }, []);

  return (
    <FallbackedLayout isLoading={ isDomainLoading } isError={ isDomainError } error={ domainError } data={ domainData }>
      { (domain): ReactElement => (
        <DomainsOverviewView
          domain={ domain }
          isDeleteDomainLoading={ isDeleteDomainLoading }
          isDisabledControlButtons={ isDomainFetching || isDeleteDomainLoading || isDomainDetachFetching }
          handleDeleteDomain={ handleDeleteDomain }
        />
      ) }
    </FallbackedLayout>
  );
});
